import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdUploadFile } from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../../components/card";
import SearchableSelect from "../../../../components/searchableSelect/SearchableSelect";
import api from "../../../../services/axios";
import {
  CobrancaAberta,
  ImportacaoExtrato,
  LancamentoNaoIdentificado,
  SugestaoCobranca,
} from "../../../../interfaces/Financial";

interface Props {
  onChange: () => void;
}

// Cobrança que pode ser marcada: a sugestão do servidor ou uma escolhida em "Outra cobrança…".
type Opcao = Omit<SugestaoCobranca, "motivo"> & { motivo?: string };

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pad2 = (n: number) => String(n).padStart(2, "0");
const competencia = (o: { monthRef: number; yearRef: number }) => `${pad2(o.monthRef)}/${o.yearRef}`;

const fmtData = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const fmtDocumento = (doc: string) => {
  if (doc.length === 14) return doc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  if (doc.length === 11) return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  return doc;
};

const mensagemDe = (error: any, padrao: string) => {
  const data = error?.response?.data;
  return typeof data === "string" && data ? data : padrao;
};

// 409: outra pessoa mexeu na cobrança ou no lançamento; a lista local está velha.
const ehConflito = (error: any) => error?.response?.status === 409;

const toastOpts = { position: "top-right", autoClose: 4000 } as const;
const centavos = (v: number) => Math.round(v * 100);

const inputCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const btnPrimary =
  "rounded-xl bg-dv-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-dv-sm transition-all hover:bg-dv-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-dv-blue-500 dark:hover:bg-dv-blue-600";
const btnGhost =
  "rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5";

const ExtratoConciliacao: React.FC<Props> = ({ onChange }) => {
  const [lancamentos, setLancamentos] = useState<LancamentoNaoIdentificado[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [importando, setImportando] = useState(false);
  const [abertas, setAbertas] = useState<CobrancaAberta[] | null>(null);
  const [extras, setExtras] = useState<Record<number, Opcao[]>>({});
  const [marcadas, setMarcadas] = useState<Record<number, number[]>>({});
  const [diferenca, setDiferenca] = useState<number | null>(null);
  const [ignorando, setIgnorando] = useState<number | null>(null);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState<number | null>(null);
  const [outraAberta, setOutraAberta] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Uma ação por vez: baixa, ignorar e importar disputam as mesmas cobranças e lançamentos.
  const ocupado = importando || enviando !== null;

  const carregar = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get<LancamentoNaoIdentificado[]>("/Financial/Extrato/NaoIdentificados");
      setLancamentos(data);
      setMarcadas({});
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const importar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    if (!arquivo) return;
    const form = new FormData();
    form.append("arquivo", arquivo);
    form.append("banco", "itau");
    setImportando(true);
    try {
      const { data } = await api.post<ImportacaoExtrato>("/Financial/Extrato/Importar", form);
      toast.success(
        `${data.novos} novos · ${data.conciliadosAutomaticamente} baixados automaticamente · ${data.naoIdentificados} para identificar · ${data.duplicados} já importados`,
        toastOpts
      );
      await carregar();
      if (data.conciliadosAutomaticamente > 0) onChange();
    } catch (error: any) {
      toast.error(mensagemDe(error, "Falha ao importar o extrato."), toastOpts);
      if (ehConflito(error)) await carregar();
    } finally {
      setImportando(false);
    }
  };

  const opcoesDe = (l: LancamentoNaoIdentificado): Opcao[] => [...l.sugestoes, ...(extras[l.id] ?? [])];

  const alternar = (lancamentoId: number, billingId: number) => {
    setDiferenca(null);
    setMarcadas((prev) => {
      const atual = prev[lancamentoId] ?? [];
      return {
        ...prev,
        [lancamentoId]: atual.includes(billingId) ? atual.filter((b) => b !== billingId) : [...atual, billingId],
      };
    });
  };

  const abrirOutra = async (lancamentoId: number) => {
    setOutraAberta(lancamentoId);
    if (abertas) return;
    try {
      const { data } = await api.get<CobrancaAberta[]>("/Financial/Extrato/CobrancasEmAberto");
      setAbertas(data);
    } catch (error: any) {
      setOutraAberta(null);
      toast.error(mensagemDe(error, "Falha ao carregar as cobranças em aberto."), toastOpts);
    }
  };

  const escolherOutra = (l: LancamentoNaoIdentificado, billingId: number) => {
    const c = abertas?.find((a) => a.billingId === billingId);
    if (!c) return;
    setOutraAberta(null);
    if (!opcoesDe(l).some((o) => o.billingId === billingId)) {
      setExtras((prev) => ({ ...prev, [l.id]: [...(prev[l.id] ?? []), c] }));
    }
    setMarcadas((prev) => {
      const atual = prev[l.id] ?? [];
      return atual.includes(billingId) ? prev : { ...prev, [l.id]: [...atual, billingId] };
    });
    setDiferenca(null);
  };

  const somaMarcadas = (l: LancamentoNaoIdentificado) => {
    const ids = marcadas[l.id] ?? [];
    return opcoesDe(l)
      .filter((o) => ids.includes(o.billingId))
      .reduce((s, o) => s + o.amount, 0);
  };

  const removerLinha = (id: number) => setLancamentos((prev) => prev.filter((l) => l.id !== id));

  const darBaixa = async (l: LancamentoNaoIdentificado) => {
    const ids = marcadas[l.id] ?? [];
    if (ids.length === 0) return;
    const soma = somaMarcadas(l);
    const difere = centavos(soma) !== centavos(l.valor);
    // Primeiro clique só avisa; a baixa com diferença exige o segundo clique.
    if (difere && diferenca !== l.id) {
      setDiferenca(l.id);
      return;
    }
    setEnviando(l.id);
    try {
      await api.post(`/Financial/Extrato/${l.id}/Conciliar`, { billingIds: ids, aceitarDiferenca: difere });
      toast.success("Baixa registrada.", toastOpts);
      removerLinha(l.id);
      setDiferenca(null);
      onChange();
    } catch (error: any) {
      toast.error(mensagemDe(error, "Falha ao dar baixa."), toastOpts);
      if (ehConflito(error)) {
        setDiferenca(null);
        await carregar();
      }
    } finally {
      setEnviando(null);
    }
  };

  const ignorar = async (l: LancamentoNaoIdentificado) => {
    if (!motivo.trim()) return;
    setEnviando(l.id);
    try {
      await api.post(`/Financial/Extrato/${l.id}/Ignorar`, { motivo: motivo.trim() });
      toast.success("Lançamento ignorado.", toastOpts);
      removerLinha(l.id);
      setIgnorando(null);
      setMotivo("");
    } catch (error: any) {
      toast.error(mensagemDe(error, "Falha ao ignorar o lançamento."), toastOpts);
      if (ehConflito(error)) await carregar();
    } finally {
      setEnviando(null);
    }
  };

  return (
    <Card extra="w-full p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">Conciliação do extrato</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Pagamentos recebidos no banco que ainda não foram ligados a uma cobrança.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            id="extrato-arquivo"
            data-testid="extrato-arquivo"
            type="file"
            accept=".ofx"
            className="hidden"
            onChange={importar}
          />
          <button
            type="button"
            className={`${btnPrimary} inline-flex items-center gap-1.5`}
            disabled={ocupado}
            onClick={() => fileRef.current?.click()}
          >
            <MdUploadFile className="h-4 w-4" aria-hidden="true" />
            {importando ? "Importando..." : "Importar extrato (OFX)"}
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col gap-3" aria-busy="true" aria-label="Carregando lançamentos">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
            ))}
          </div>
        ) : loadError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          >
            Não foi possível carregar os pagamentos.{" "}
            <button type="button" className="font-semibold underline" onClick={carregar}>
              Tentar novamente
            </button>
          </div>
        ) : lancamentos.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Nenhum pagamento para identificar.</p>
        ) : (
          lancamentos.map((l) => {
            const ids = marcadas[l.id] ?? [];
            const soma = somaMarcadas(l);
            const pedeConfirmar = diferenca === l.id;
            return (
              <div
                key={l.id}
                className="rounded-xl border border-gray-200 p-4 dark:border-white/10"
                data-testid={`lancamento-${l.id}`}
              >
                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                  {fmtData(l.dtLancamento)} · {brl(l.valor)}
                </p>
                <p className="break-words text-sm text-gray-600 dark:text-gray-400">
                  {l.descricao}
                  {l.documentoPagador ? ` · ${fmtDocumento(l.documentoPagador)}` : ""}
                </p>

                <ul className="mt-3 flex flex-col gap-2">
                  {opcoesDe(l).map((o) => {
                    const idInput = `sug-${l.id}-${o.billingId}`;
                    return (
                      <li key={o.billingId} className="flex items-start gap-2">
                        <input
                          id={idInput}
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/20"
                          checked={ids.includes(o.billingId)}
                          onChange={() => alternar(l.id, o.billingId)}
                        />
                        <label htmlFor={idInput} className="text-sm text-gray-700 dark:text-gray-300">
                          {o.clientName} · {o.projectName ?? "sem projeto"} · {competencia(o)} · {brl(o.amount)}
                          {o.invoiceNumber ? ` · NF nº ${o.invoiceNumber}` : ""}
                          {o.motivo ? <span className="text-gray-500 dark:text-gray-400"> · {o.motivo}</span> : null}
                        </label>
                      </li>
                    );
                  })}
                  {opcoesDe(l).length === 0 && (
                    <li className="text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma sugestão. Escolha a cobrança em "Outra cobrança…".
                    </li>
                  )}
                </ul>

                <div className="mt-2">
                  {outraAberta === l.id ? (
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`outra-${l.id}`} className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Outra cobrança
                      </label>
                      <SearchableSelect
                        options={(abertas ?? []).map((a) => ({
                          label: `${a.clientName} · ${a.projectName ?? "sem projeto"} · ${competencia(a)} · ${brl(a.amount)}${a.invoiceNumber ? ` · NF nº ${a.invoiceNumber}` : ""}`,
                          value: a.billingId,
                        }))}
                        value={null}
                        disabled={!abertas}
                        placeholder={abertas ? "Busque por cliente, projeto ou NF" : "Carregando..."}
                        onChange={(v) => v !== null && escolherOutra(l, Number(v))}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="text-xs font-semibold text-dv-blue-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:text-dv-blue-400"
                      onClick={() => abrirOutra(l.id)}
                    >
                      Outra cobrança…
                    </button>
                  )}
                </div>

                {pedeConfirmar && (
                  <p
                    role="alert"
                    className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
                  >
                    O crédito de {brl(l.valor)} difere da soma ({brl(soma)}).
                  </p>
                )}

                {ignorando === l.id ? (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex flex-1 flex-col gap-1">
                      <label htmlFor={`motivo-${l.id}`} className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Motivo para ignorar
                      </label>
                      <input
                        id={`motivo-${l.id}`}
                        type="text"
                        className={inputCls}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                      />
                    </div>
                    <button type="button" className={btnPrimary} disabled={!motivo.trim() || ocupado} onClick={() => ignorar(l)}>
                      Confirmar
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() => {
                        setIgnorando(null);
                        setMotivo("");
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" className={btnPrimary} disabled={ids.length === 0 || ocupado} onClick={() => darBaixa(l)}>
                      {pedeConfirmar ? "Dar baixa assim mesmo" : "Dar baixa"}
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={ocupado}
                      onClick={() => {
                        setIgnorando(l.id);
                        setMotivo("");
                      }}
                    >
                      Ignorar
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default ExtratoConciliacao;
